import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { Plus, Trash2, Edit, CheckCircle, Clock, History, Check } from 'lucide-react';
import type { Quest } from '../lib/provider/types';
import { isToday, isWeeklyQuestDay } from '../lib/utils';
import { useTranslation } from '../hooks/useTranslation';
import { Avatar } from '../components/Avatar';

export const ParentQuests: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [isAdding, setIsAdding] = useState(false);
    const [editingQuestId, setEditingQuestId] = useState<string | null>(null);
    const formRef = useRef<HTMLDivElement>(null);

    // Form state
    const [title, setTitle] = useState('');
    const [points, setPoints] = useState(50);
    const [frequency, setFrequency] = useState<'one_time' | 'daily' | 'weekly'>('one_time');
    const [weeklyDay, setWeeklyDay] = useState(1); // Default to Monday
    const [dueDate, setDueDate] = useState('');
    const [dailyStartHour, setDailyStartHour] = useState<number>(6);
    const [dailyEndHour, setDailyEndHour] = useState<number>(22);
    const [assignedKidIds, setAssignedKidIds] = useState<string[]>([]);
    const [initialAssignedKidIds, setInitialAssignedKidIds] = useState<string[]>([]);

    const { data: quests, isLoading: loadingQuests } = useQuery({
        queryKey: ['quests', session.user?.household_id],
        queryFn: () => provider.getQuests(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: members } = useQuery({
        queryKey: ['members', session.user?.household_id],
        queryFn: () => provider.getMembers(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: completions } = useQuery({
        queryKey: ['completions', session.user?.household_id],
        queryFn: () => provider.getCompletions(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const kids = members?.filter(m => m.role === 'kid') || [];

    const approvedCompletions = completions?.filter(c => c.status === 'approved') || [];

    // Filter quests - matching KidHome logic
    const completedOneTimeQuestIds = new Set(
        approvedCompletions
            .filter(c => c.quest.frequency === 'one_time')
            .map(c => c.quest_id)
    );

    const activeQuests = quests?.filter(q => {
        // Filter out completed one-time quests
        if (completedOneTimeQuestIds.has(q.id)) return false;
        
        // For daily quests, filter out those completed today (approved or pending)
        if (q.frequency === 'daily') {
            const completedToday = completions?.some(c => 
                c.quest_id === q.id && 
                (c.status === 'approved' || c.status === 'pending') && 
                isToday(c.completed_at)
            );
            return !completedToday;
        }
        
        return true;
    }) || [];

    const createQuest = useMutation({
        mutationFn: (newQuest: Omit<Quest, 'id' | 'created_at'>) => provider.createQuest(newQuest),
        onSuccess: async (data) => {
            if (assignedKidIds.length > 0) {
                await Promise.all(assignedKidIds.map((kidId) => provider.assignQuest(data.id, kidId)));
            }
            queryClient.invalidateQueries({ queryKey: ['quests'] });
            resetForm();
        }
    });

    const updateQuest = useMutation({
        mutationFn: async ({ id, updates, nextAssignedKidIds, prevAssignedKidIds }: { id: string, updates: Partial<Quest>, nextAssignedKidIds: string[], prevAssignedKidIds: string[] }) => {
            await provider.updateQuest(id, updates);

            const prev = new Set(prevAssignedKidIds);
            const next = new Set(nextAssignedKidIds);

            const toAssign = nextAssignedKidIds.filter((kidId) => !prev.has(kidId));
            const toUnassign = prevAssignedKidIds.filter((kidId) => !next.has(kidId));

            await Promise.all([
                ...toAssign.map((kidId) => provider.assignQuest(id, kidId)),
                ...toUnassign.map((kidId) => provider.unassignQuest(id, kidId)),
            ]);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quests'] });
            resetForm();
        }
    });

    const deleteQuest = useMutation({
        mutationFn: (id: string) => provider.deleteQuest(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quests'] });
        }
    });

    const assignQuest = useMutation({
        mutationFn: ({ questId, kidId }: { questId: string, kidId: string }) => provider.assignQuest(questId, kidId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quests'] });
        }
    });

    const unassignQuest = useMutation({
        mutationFn: ({ questId, kidId }: { questId: string, kidId: string }) => provider.unassignQuest(questId, kidId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quests'] });
        }
    });

    const parentComplete = useMutation({
        mutationFn: async ({ questId, kidId }: { questId: string, kidId: string }) => {
            const completion = await provider.completeQuest({
                quest_id: questId,
                kid_user_id: kidId,
            });
            await provider.approveCompletion(completion.id, session.user!.id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quests'] });
            queryClient.invalidateQueries({ queryKey: ['completions'] });
        },
        onError: (error: any) => {
            console.error('Failed to complete quest:', error);
            alert(error?.message || 'Failed to complete quest');
        }
    });
    const deleteCompletion = useMutation({
        mutationFn: (id: string) => provider.deleteCompletion(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['completions'] });
            queryClient.invalidateQueries({ queryKey: ['quests'] });
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to delete activity entry');
        },
    });

    const resetForm = () => {
        setIsAdding(false);
        setEditingQuestId(null);
        setTitle('');
        setPoints(50);
        setFrequency('one_time');
        setWeeklyDay(1);
        setDueDate('');
        setDailyStartHour(6);
        setDailyEndHour(22);
        setAssignedKidIds([]);
        setInitialAssignedKidIds([]);
    };

    const handleEdit = (quest: Quest) => {
        setEditingQuestId(quest.id);
        setTitle(quest.title);
        setPoints(quest.points);
        setFrequency(quest.frequency);
        setWeeklyDay(quest.weekly_day || 1);
        setDueDate(quest.due_date || '');
        setDailyStartHour(typeof quest.daily_start_hour === 'number' ? quest.daily_start_hour : 6);
        setDailyEndHour(typeof quest.daily_end_hour === 'number' ? quest.daily_end_hour : 22);
        const currentAssigned = (quest.assignments || []).map((a) => a.kid_user_id);
        setAssignedKidIds(currentAssigned);
        setInitialAssignedKidIds(currentAssigned);
        setIsAdding(true);
        setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    const handleDelete = (id: string) => {
        if (confirm('Are you sure you want to delete this quest?')) {
            deleteQuest.mutate(id);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const normalizedDaily = frequency === 'daily'
            ? {
                start: Math.max(6, Math.min(22, Math.floor(dailyStartHour))),
                end: Math.max(6, Math.min(22, Math.floor(dailyEndHour)))
            }
            : null;

        if (normalizedDaily) {
            if (normalizedDaily.start >= normalizedDaily.end) {
                alert('Daily quest time must be between 6 and 22 hours and start must be before end');
                return;
            }
        }

        const questData = {
            household_id: session.user!.household_id,
            title,
            description: '',
            points,
            frequency,
            weekly_day: frequency === 'weekly' ? weeklyDay : undefined,
            due_date: frequency === 'one_time' ? (dueDate ? dueDate : null) : null,
            daily_start_hour: normalizedDaily ? normalizedDaily.start : null,
            daily_end_hour: normalizedDaily ? normalizedDaily.end : null,
            active: true,
            created_by: session.user!.id,
        };

        if (editingQuestId) {
            updateQuest.mutate({
                id: editingQuestId,
                updates: questData,
                nextAssignedKidIds: assignedKidIds,
                prevAssignedKidIds: initialAssignedKidIds,
            });
        } else {
            createQuest.mutate(questData);
        }
    };

    if (loadingQuests) return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;

    const getFrequencyLabel = (freq: string, weeklyDay?: number) => {
        if (freq === 'daily') return t.parentQuests.daily;
        if (freq === 'weekly') {
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            return `${t.parentQuests.weekly} (${weeklyDay !== undefined ? days[weeklyDay] : 'Monday'})`;
        }
        return t.parentQuests.oneTime;
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-slate-900">{t.parentQuests.title}</h1>
                <button
                    onClick={() => {
                        setIsAdding((prev) => {
                            const next = !prev;
                            if (next) {
                                setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                            }
                            return next;
                        });
                    }}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                    <Plus size={20} />
                    {t.parentQuests.createQuest}
                </button>
            </div>

            {isAdding && (
                <div ref={formRef} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-4">
                    <h2 className="text-lg font-bold mb-4">{editingQuestId ? t.parentQuests.editQuest : t.parentQuests.newQuest}</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t.parentQuests.questTitle}</label>
                            <input
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Clean your room"
                                className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t.common.points}</label>
                                <input
                                    type="number"
                                    required
                                    value={points}
                                    onChange={(e) => setPoints(Number(e.target.value))}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t.parentQuests.frequency}</label>
                                <select
                                    value={frequency}
                                    onChange={(e) => setFrequency(e.target.value as any)}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="one_time">{t.parentQuests.oneTime}</option>
                                    <option value="daily">{t.parentQuests.daily}</option>
                                    <option value="weekly">{t.parentQuests.weekly}</option>
                                </select>
                            </div>
                        </div>
                        {frequency === 'weekly' && (
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Day of Week</label>
                                <select
                                    value={weeklyDay}
                                    onChange={(e) => setWeeklyDay(Number(e.target.value))}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value={0}>Sunday</option>
                                    <option value={1}>Monday</option>
                                    <option value={2}>Tuesday</option>
                                    <option value={3}>Wednesday</option>
                                    <option value={4}>Thursday</option>
                                    <option value={5}>Friday</option>
                                    <option value={6}>Saturday</option>
                                </select>
                            </div>
                        )}
                        {frequency === 'one_time' && (
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Due date (optional)</label>
                                <input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                        )}
                        {frequency === 'daily' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Available from (hour)</label>
                                    <input
                                        type="number"
                                        min={6}
                                        max={22}
                                        step={1}
                                        required
                                        value={dailyStartHour}
                                        onChange={(e) => setDailyStartHour(Number(e.target.value))}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Available until (hour)</label>
                                    <input
                                        type="number"
                                        min={6}
                                        max={22}
                                        step={1}
                                        required
                                        value={dailyEndHour}
                                        onChange={(e) => setDailyEndHour(Number(e.target.value))}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t.parentQuests.assignTo}</label>
                            <div className="flex flex-wrap gap-2">
                                {kids.map((kid) => {
                                    const isSelected = assignedKidIds.includes(kid.user_id);
                                    return (
                                        <button
                                            key={kid.user_id}
                                            type="button"
                                            onClick={() => {
                                                setAssignedKidIds((prev) =>
                                                    prev.includes(kid.user_id)
                                                        ? prev.filter((id) => id !== kid.user_id)
                                                        : [...prev, kid.user_id]
                                                );
                                            }}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${isSelected
                                                ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                                                : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:bg-indigo-50/40'
                                                }`}
                                            title={kid.display_name}
                                        >
                                            <Avatar name={kid.display_name} size="sm" variant="kid" />
                                            <span className="text-sm font-bold">{kid.display_name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="submit"
                                disabled={createQuest.isPending || updateQuest.isPending}
                                className="flex-1 bg-purple-600 text-white py-2 rounded-lg font-bold hover:bg-purple-700 transition-colors disabled:opacity-50"
                            >
                                {createQuest.isPending || updateQuest.isPending ? t.common.loading : (editingQuestId ? t.parentQuests.editQuest : t.parentQuests.createQuest)}
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-6 py-2 rounded-lg font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                                {t.common.cancel}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="space-y-8">
                {/* Active Quests */}
                <div className="space-y-4">
                    <h2 className="text-xl font-bold text-slate-700 flex items-center gap-2">
                        <Clock size={20} className="text-purple-500" />
                        {t.parentQuests.activeQuests}
                    </h2>
                    <div className="grid gap-4">
                        {activeQuests.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 border border-slate-200 text-center">
                                <p className="text-slate-400 font-medium">{t.parentQuests.noActiveQuests}</p>
                            </div>
                        ) : (
                            activeQuests.map((quest) => {
                                const questCompletions = completions?.filter(c => c.quest_id === quest.id) || [];
                                const hasPending = questCompletions.some(c => c.status === 'pending');

                                const dailyTimeLabel = (quest.frequency === 'daily' && typeof quest.daily_start_hour === 'number' && typeof quest.daily_end_hour === 'number')
                                    ? `${quest.daily_start_hour}:00-${quest.daily_end_hour}:00`
                                    : null;

                                return (
                                    <div key={quest.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-xl font-bold text-slate-900">{quest.title}</h3>
                                                <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded uppercase font-bold tracking-wider">
                                                    {getFrequencyLabel(quest.frequency, quest.weekly_day)}
                                                </span>
                                                {hasPending && (
                                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-[10px] rounded uppercase font-black tracking-widest flex items-center gap-1">
                                                        <Clock size={10} /> {t.parentDashboard.questQueue}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 mt-2">
                                                <span className="text-purple-600 font-bold">{quest.points} {t.common.points}</span>
                                                {dailyTimeLabel && (
                                                    <>
                                                        <span className="text-slate-200">|</span>
                                                        <span className="text-xs text-slate-400">{dailyTimeLabel}</span>
                                                    </>
                                                )}
                                                {quest.creator_name && (
                                                    <>
                                                        <span className="text-slate-200">|</span>
                                                        <span className="text-xs text-slate-400">{t.parentQuests.createdBy} {quest.creator_name}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-slate-400">{t.parentQuests.assignToKid}</span>
                                                <div className="flex gap-2">
                                                    {kids.map(kid => {
                                                        const isAssigned = quest.assignments?.some(a => a.kid_user_id === kid.user_id);
                                                        const kidCompletions = questCompletions.filter(c => c.kid_user_id === kid.user_id);

                                                        let isDone = false;
                                                        if (quest.frequency === 'daily') {
                                                            isDone = kidCompletions.some(c => (c.status === 'approved' || c.status === 'pending') && isToday(c.completed_at));
                                                        } else if (quest.frequency === 'weekly') {
                                                            // For weekly quests, only check if completed if today is the assigned day
                                                            if (quest.weekly_day !== undefined && isWeeklyQuestDay(quest.weekly_day)) {
                                                                isDone = kidCompletions.some(c => (c.status === 'approved' || c.status === 'pending') && isToday(c.completed_at));
                                                            }
                                                        } else if (quest.frequency === 'one_time') {
                                                            isDone = kidCompletions.some(c => c.status === 'approved');
                                                        }

                                                        const kidHasPending = kidCompletions.some(c => c.status === 'pending');

                                                        return (
                                                            <div key={kid.user_id} className="relative group/kid">
                                                                <button
                                                                    onClick={() => {
                                                                        if (isAssigned) {
                                                                            unassignQuest.mutate({ questId: quest.id, kidId: kid.user_id });
                                                                        } else {
                                                                            assignQuest.mutate({ questId: quest.id, kidId: kid.user_id });
                                                                        }
                                                                    }}
                                                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all relative ${isAssigned
                                                                        ? 'bg-purple-600 text-white ring-2 ring-purple-200 ring-offset-1'
                                                                        : 'bg-slate-100 text-slate-600 hover:bg-purple-100 hover:text-purple-600'
                                                                        } ${(isDone || kidHasPending) ? 'ring-2 ring-emerald-400 shadow-sm' : ''}`}
                                                                    title={isAssigned ? `${kid.display_name} (${t.parentQuests.unassign})` : `${t.parentQuests.assign} ${kid.display_name}`}
                                                                >
                                                                    {kid.display_name[0]}
                                                                    {(isDone || kidHasPending) && (
                                                                        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white flex items-center justify-center ${kidHasPending ? 'bg-amber-400' : 'bg-emerald-500'}`}>
                                                                            <Check size={8} className="text-white" strokeWidth={4} />
                                                                        </div>
                                                                    )}
                                                                </button>
                                                                {isAssigned && !isDone && !kidHasPending && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            parentComplete.mutate({ questId: quest.id, kidId: kid.user_id });
                                                                        }}
                                                                        className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center transition-opacity shadow-sm hover:scale-110 hover:bg-emerald-600"
                                                                        title={t.parentQuests.markCompleted}
                                                                    >
                                                                        <Check size={10} strokeWidth={4} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <div className="h-8 w-px bg-slate-100 mx-2" />
                                                <button
                                                    onClick={() => handleEdit(quest)}
                                                    className="p-2 text-slate-400 hover:text-slate-600"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(quest.id)}
                                                    className="p-2 text-slate-400 hover:text-red-500"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Completed Quests / History */}
                {completions && completions.length > 0 && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-slate-700 flex items-center gap-2">
                            <History size={20} className="text-slate-400" />
                            {t.parentQuests.recentActivity}
                        </h2>
                        <div className="grid gap-4 opacity-75 grayscale-[0.5]">
                            {completions.slice(0, 10).map((c) => (
                                <div key={c.id} className="bg-slate-50 rounded-xl p-6 border border-slate-200 shadow-sm flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-xl font-bold text-slate-500 line-through decoration-slate-300">{c.quest.title}</h3>
                                            <span className={`px-2 py-0.5 text-[10px] rounded uppercase font-black tracking-widest flex items-center gap-1 ${c.status === 'approved' ? 'bg-emerald-100 text-emerald-600' :
                                                c.status === 'pending' ? 'bg-amber-100 text-amber-600' : 'bg-rose-100 text-rose-600'
                                                }`}>
                                                {c.status === 'approved' ? <CheckCircle size={10} /> : c.status === 'pending' ? <Clock size={10} /> : null}
                                                {c.status === 'approved' ? t.common.approved : c.status === 'pending' ? t.common.pending : t.common.rejected}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 mt-2">
                                            <span className="text-slate-400 font-bold">{c.quest.points} {t.common.pts}</span>
                                            <span className="text-slate-200">|</span>
                                            <span className="text-xs text-slate-400 italic">
                                                {t.parentDashboard.completedBy} {c.member.display_name} • {new Date(c.completed_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (confirm('Are you sure you want to delete this activity entry?')) {
                                                deleteCompletion.mutate(c.id);
                                            }
                                        }}
                                        className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                                        title={t.common.delete}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
